import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Star, Linkedin, Search, Filter } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

export default function ClientsDirectory({ data = [] }) {
  const [q, setQ] = useState("");
  const [minRating, setMinRating] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const source = data.length ? data : realClients;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return source.filter((c) => {
      const hay = `${c.name} ${c.title ?? ""} ${c.company ?? ""}`.toLowerCase();
      const passText = term ? hay.includes(term) : true;
      const passRating = (c.rating ?? 0) >= minRating;
      return passText && passRating;
    });
  }, [q, minRating, source]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);

  const resetToFirst = () => setPage(1);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          {/* Page header */}
          <div className="text-center mb-10">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-indigo-600 via-fuchsia-600 to-purple-600 bg-clip-text text-transparent"
            >
              Our Clients
            </motion.h1>
            <p className="mt-3 text-gray-600">
              Explore our full roster — search, filter by rating, and reach out
              on LinkedIn.
            </p>
          </div>

          {/* Controls */}
          <div className="grid gap-3 sm:grid-cols-3 mb-8">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2"
                size={18}
              />
              <input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  resetToFirst();
                }}
                placeholder="Search name, role, company..."
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              />
            </div>

            <div className="relative">
              <Filter
                className="absolute left-3 top-1/2 -translate-y-1/2"
                size={18}
              />
              <select
                value={minRating}
                onChange={(e) => {
                  setMinRating(Number(e.target.value));
                  resetToFirst();
                }}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value={0}>All ratings</option>
                <option value={5}>5★ only</option>
                <option value={4}>4★ and up</option>
                <option value={3}>3★ and up</option>
              </select>
            </div>

            <div className="flex items-center justify-end">
              <span className="text-sm text-gray-500">
                {filtered.length} results
              </span>
            </div>
          </div>

          {/* Cards */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pageData.map((c, idx) => (
              <motion.article
                key={c.name + idx}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (idx % pageSize) * 0.02 }}
                className="rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-lg transition p-5"
              >
                <div className="flex items-center gap-4">
                  <img
                    src={c.img}
                    alt={c.name}
                    className="h-12 w-12 rounded-full object-cover border"
                  />
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {c.name}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">
                      {c.title}
                      {c.company ? ` • ${c.company}` : ""}
                    </p>
                  </div>
                </div>

                {c.quote && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-3">
                    “{c.quote}”
                  </p>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: c.rating ?? 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className="text-yellow-400"
                        fill="currentColor"
                      />
                    ))}
                  </div>

                  {c.linkedin && (
                    <a
                      href={c.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      <Linkedin size={16} /> Connect
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-2 rounded-lg border border-gray-300 disabled:opacity-40 bg-white"
            >
              Prev
            </button>

            <div className="px-3 py-2 text-sm text-gray-600">
              Page {page} / {totalPages}
            </div>

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-2 rounded-lg border border-gray-300 disabled:opacity-40 bg-white"
            >
              Next
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

/* ---------------- REAL LINKEDIN DATA ---------------- */

const realClients = [
  {
    name: "Shubham Singh",
    quote:
      "Their profile improvements helped me present my storage & infrastructure experience clearly and professionally.",
    img: "https://media.licdn.com/dms/image/v2/D5603AQEpkjWoox3jvg/profile-displayphoto-shrink_400_400/B56ZQ5u42nHQAg-/0/1736135382574?e=1767225600&v=beta&t=RNab1YrlrE4TrQdI0kMwDzhZRIUsHNo5GFSI2PQ6oes",
    rating: 4,
    linkedin: "https://www.linkedin.com/in/shubham-singh-49629a230/",
  },
  {
    name: "Shweta Benke",
    quote:
      "Clear, structured, and impactful profile content. It significantly improved how recruiters engage with my profile.",
    img: "https://media.licdn.com/dms/image/v2/D5635AQHaiMNieNt29Q/profile-framedphoto-shrink_400_400/B56ZaYll0QGkAc-/0/1746316713640?e=1766250000&v=beta&t=ms-Y7sa8Jhy40AEjUc7qyespMUzr8MEDKdTQOfIG2dY",
    rating: 5,
    linkedin: "https://www.linkedin.com/in/shweta-benke-266a7a203/",
  },
  {
    name: "Shipra Jaiswal",
    quote:
      "They helped refine my professional story and highlight my strengths. Strongly recommend their services!",
    img: "https://media.licdn.com/dms/image/v2/D5603AQEwgfKAODOsSA/profile-displayphoto-scale_400_400/B56Zm5gcOLJ8Ag-/0/1759753915899?e=1767225600&v=beta&t=U-2HCgikb8PvkzDbkp1MWdm0BupPoYtmsZUu8HxTfI0",
    rating: 4,
    linkedin: "https://www.linkedin.com/in/shipra-jaiswal-883286259/",
  },
  {
    name: "Mohammad Javed",
    quote:
      "Amazing improvement in clarity and structure. The optimized profile truly reflects my technical capabilities.",
    img: "https://media.licdn.com/dms/image/v2/D4D03AQGK0S2feom4sw/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1696508980444?e=1767225600&v=beta&t=_ESL7KJeuhkJyZ_nusEsl4OC1nyFWg6aLYKUEpCf5k4",
    rating: 3,
    linkedin: "https://www.linkedin.com/in/mohammad-javed-517a991a0/",
  },
  {
    name: "Shikha Singh",
    quote:
      "Senior Technical Specialist at HCLTech | VMware & Windows | AWS & Azure Cloud AdministratorSenior Technical Specialist at HCLTech | VMware & Windows | AWS & Azure Cloud Administrator",
    img: "https://media.licdn.com/dms/image/v2/D5603AQGoMM8oO-NBjQ/profile-displayphoto-shrink_800_800/B56ZYQYWPEGoAg-/0/1744031541252?e=1767225600&v=beta&t=ljG2v1fWNIL0BRBKsUDKtGpQq--IGjJMhBVPMeB9mDM",
    rating: 5,
    linkedin: "https://www.linkedin.com/in/shikhaasingh12/",
  },
];
